/**
 * The sandbox dev inspector's client side (SANDBOX_DEV_INSPECTOR): what this tab's executor
 * returned for each repl cell it ran (C1 `ReplResult.calls`, CM2's client `journal`), kept in
 * memory by sandbox call id so the inspector can show it beside the server's journal. Nothing is
 * kept while the flag is off (read at each cell: `recordCellsWhile`), nothing survives a reload,
 * only the latest cells are kept, and each value is cut to `VALUE_MAX` as it is kept.
 */
import type { CellCall, JournalEntry, ReplResult } from "../core/types";

/** A journal entry as kept: a string value longer than `VALUE_MAX` is cut, with `elided` set. */
export type RecordedEntry = JournalEntry & { elided?: boolean };

export interface CellRecord {
  calls: CellCall[];
  journal: RecordedEntry[];
}

/** CM2's client-local journal (code-cells), not yet on this branch's C1 `ReplResult`. */
type RecordedResult = ReplResult & { journal?: JournalEntry[] };

const KEPT = 50;
/** As the server cuts its own values (apps/sandbox/inspector.py VALUE_MAX). */
export const VALUE_MAX = 16_000;
const records = new Map<string, CellRecord>();
const listeners = new Set<() => void>();
let recording: () => boolean = () => false;

const notify = () => listeners.forEach((listener) => listener());

function clear(): void {
  if (!records.size) return;
  records.clear();
  notify();
}

/** Records cells while `on()` holds; returns the stop, which drops what was kept. */
export function recordCellsWhile(on: () => boolean): () => void {
  recording = on;
  return () => {
    recording = () => false;
    clear();
  };
}

/** Keeps a repl cell's calls and journal under its sandbox call id, while recording is on. */
export function recordCell(sandboxCallId: string, result: RecordedResult): void {
  if (!recording()) return clear();
  if (!result.calls && !result.journal) return;
  records.delete(sandboxCallId);
  records.set(sandboxCallId, {
    calls: result.calls ?? [],
    journal: (result.journal ?? []).map(capped),
  });
  if (records.size > KEPT) records.delete(records.keys().next().value!);
  notify();
}

function capped(entry: JournalEntry): RecordedEntry {
  if (typeof entry.value !== "string" || entry.value.length <= VALUE_MAX) return entry;
  return { ...entry, value: entry.value.slice(0, VALUE_MAX), elided: true } as RecordedEntry;
}

export function cellRecord(sandboxCallId: string): CellRecord | null {
  return records.get(sandboxCallId) ?? null;
}

export function subscribeCellRecords(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
